import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft,
  MessageSquare,
  Phone,
  Plus,
  RotateCcw,
  Send,
  Trash2,
  User,
  UserPlus,
} from 'lucide-react';
import { Button } from '@/components/button';
import { Input } from '@/components/input';
import { Textarea } from '@/components/textarea';
import { Dialog } from '@/components/dialog';
import { Badge } from '@/components/badge';
import { PageHeader } from '@/components/page-header';
import { EmptyState } from '@/components/empty-state';
import { SkeletonList } from '@/components/skeleton';
import { IdentifyDialog } from '@/components/identify-dialog';
import { Menu } from '@/components/menu';
import { Switch } from '@/components/switch';
import { useConfirm } from '@/components/confirm';
import { useToast } from '@/components/toast';
import { KIND_LABELS, kindTone } from '@/lib/contacts-directory';
import { useFormError } from '@/lib/form-error';
import { cn } from '@/lib/cn';
import { formatDate } from '@/lib/format';
import {
  useDeleteMessage,
  useDeleteThread,
  useMarkThreadRead,
  useMessageThreads,
  usePurgeMessages,
  useRestoreThread,
  useSendMessage,
  useThread,
} from '@/lib/messages';
import { useSoftphone } from '@/lib/softphone';
import type { MessageData, MessageThreadData } from '@/types/api';

/** +15095551234 -> (509) 555-1234; leaves anything non-US as-is. */
function formatPhone(e164: string): string {
  const m = /^\+1(\d{3})(\d{3})(\d{4})$/.exec(e164);
  return m ? `(${m[1]}) ${m[2]}-${m[3]}` : e164;
}

function threadName(t: MessageThreadData): string {
  return t.contact_name ?? t.client_name ?? t.lead_name ?? formatPhone(t.party_number);
}

/** Initials from the letters in a name; empty for a phone-only (unidentified) label. */
function initials(label: string): string {
  const words = label
    .replace(/[^\p{L}\s]/gu, ' ')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  const first = words[0]?.[0] ?? '';
  const second = words.length > 1 ? words[words.length - 1][0] : '';
  return (first + second).toUpperCase();
}

export function Messages() {
  const [showDeleted, setShowDeleted] = useState(false);
  const threads = useMessageThreads(showDeleted ? 'deleted' : undefined);
  const [selected, setSelected] = useState<string | null>(null);
  const [composing, setComposing] = useState(false);
  const markRead = useMarkThreadRead();
  const purge = usePurgeMessages();
  const confirm = useConfirm();
  const toast = useToast();

  const rows = threads.data ?? [];
  const active = rows.find((t) => t.party_number === selected) ?? null;

  function openThread(number: string) {
    setSelected(number);
    const t = rows.find((x) => x.party_number === number);
    // Reading the trash must not mark anything read: those messages are on
    // their way out, and the badge should reflect the live inbox only.
    if (t && t.unread_count > 0 && !showDeleted) markRead.mutate(number);
  }

  async function handlePurge() {
    const ok = await confirm({
      title: 'Permanently delete every deleted text?',
      body: 'This cannot be undone. The messages go, and Twilio is asked to delete them on its side, which takes any picture messages with them.',
      confirmLabel: 'Delete for good',
      tone: 'danger',
    });
    if (!ok) return;
    const purged = await purge.mutateAsync();
    setSelected(null);
    toast.success(`${purged} message${purged === 1 ? '' : 's'} deleted for good`);
  }

  return (
    <div className="mx-auto w-full max-w-7xl space-y-4">
      <PageHeader
        icon={MessageSquare}
        title="Messages"
        subtitle="Text conversations on the Mehrutherm business line."
        action={
          <div className="flex items-center gap-2">
            {showDeleted && rows.length > 0 ? (
              <Button variant="danger" disabled={purge.isPending} onClick={handlePurge}>
                <Trash2 className="size-4" /> Empty deleted
              </Button>
            ) : null}
            <Button onClick={() => setComposing(true)}>
              <Plus className="size-4" /> New message
            </Button>
          </div>
        }
      />

      <label className="flex items-center gap-2 text-sm">
        <Switch
          checked={showDeleted}
          onChange={(next) => {
            setShowDeleted(next);
            setSelected(null);
          }}
          label="Show deleted"
        />
        <span className="text-[var(--color-foreground-muted)]">Show deleted</span>
      </label>

      <div className="flex h-[calc(100vh-10rem)] overflow-hidden rounded-xl border border-[var(--color-border)] bg-[var(--color-surface)]">
        {/* Thread list */}
        <div
          className={cn(
            'w-full flex-col overflow-y-auto border-r border-[var(--color-border)] md:flex md:w-80',
            selected ? 'hidden md:flex' : 'flex',
          )}
        >
          {threads.isLoading ? (
            <div className="p-3">
              <SkeletonList rows={6} />
            </div>
          ) : rows.length === 0 ? (
            <EmptyState
              icon={MessageSquare}
              title={showDeleted ? 'Nothing deleted' : 'No conversations yet'}
              description={
                showDeleted
                  ? 'Conversations you delete wait here until you empty them for good.'
                  : 'Texts to and from your business line will show up here.'
              }
            />
          ) : (
            <ul className="divide-y divide-[var(--color-border)]">
              {rows.map((t) => (
                <li key={t.party_number}>
                  <ThreadRow
                    thread={t}
                    active={t.party_number === selected}
                    onClick={() => openThread(t.party_number)}
                  />
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* Conversation */}
        <div className={cn('flex-1 flex-col', selected ? 'flex' : 'hidden md:flex')}>
          {active ? (
            <Conversation
              thread={active}
              deleted={showDeleted}
              onBack={() => setSelected(null)}
            />
          ) : (
            <div className="flex flex-1 items-center justify-center p-6 text-sm text-muted-foreground">
              Select a conversation to read and reply.
            </div>
          )}
        </div>
      </div>

      <NewMessageDialog
        open={composing}
        onClose={() => setComposing(false)}
        onStarted={(number) => {
          setComposing(false);
          setSelected(number);
        }}
      />
    </div>
  );
}

function ThreadRow({
  thread,
  active,
  onClick,
}: {
  thread: MessageThreadData;
  active: boolean;
  onClick: () => void;
}) {
  const name = threadName(thread);
  const preview =
    (thread.last_direction === 'outbound' ? 'You: ' : '') + (thread.last_body || '');

  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'flex w-full items-center gap-3 px-3 py-3 text-left transition-colors hover:bg-muted/40',
        active && 'bg-muted/60',
      )}
    >
      <span className="inline-flex size-9 shrink-0 items-center justify-center rounded-full bg-[var(--color-surface-muted)] text-xs font-medium text-[var(--color-foreground-muted)]">
        {initials(name) || <User className="size-4" />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center justify-between gap-2">
          <span className={cn('truncate text-sm', thread.unread_count > 0 ? 'font-semibold' : 'font-medium')}>
            {name}
          </span>
          <span className="shrink-0 text-[11px] text-muted-foreground">{formatDate(thread.last_at)}</span>
        </span>
        <span className="mt-0.5 flex items-center justify-between gap-2">
          <span
            className={cn(
              'truncate text-xs',
              thread.unread_count > 0 ? 'text-foreground' : 'text-muted-foreground',
            )}
          >
            {preview}
          </span>
          {thread.unread_count > 0 ? (
            <span className="inline-flex min-w-[1.25rem] shrink-0 items-center justify-center rounded-full bg-[var(--color-primary)] px-1.5 text-[10px] font-medium tabular-nums text-white">
              {thread.unread_count}
            </span>
          ) : null}
        </span>
      </span>
    </button>
  );
}

function Conversation({
  thread,
  deleted,
  onBack,
}: {
  thread: MessageThreadData;
  deleted: boolean;
  onBack: () => void;
}) {
  const { data: messages, isLoading } = useThread(thread.party_number, deleted ? 'deleted' : undefined);
  const send = useSendMessage();
  const sp = useSoftphone();
  const removeThread = useDeleteThread();
  const restoreThread = useRestoreThread();
  const confirm = useConfirm();
  const toast = useToast();
  const [body, setBody] = useState('');
  const [identifying, setIdentifying] = useState(false);
  const scrollRef = useRef<HTMLDivElement>(null);
  const name = threadName(thread);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight });
  }, [messages]);

  async function handleSend(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const text = body.trim();
    if (!text) return;
    setBody('');
    try {
      await send.mutateAsync({ to: thread.party_number, body: text });
    } catch {
      setBody(text);
      toast.error("Couldn't send", 'Check the number and try again.');
    }
  }

  return (
    <>
      <div className="flex items-center gap-3 border-b border-[var(--color-border)] px-4 py-3">
        <button
          type="button"
          onClick={onBack}
          className="inline-flex size-8 items-center justify-center rounded-md text-muted-foreground hover:bg-muted/50 md:hidden"
          aria-label="Back to conversations"
        >
          <ArrowLeft className="size-4" />
        </button>
        <div className="min-w-0">
          <div className="flex items-center gap-2">
            {thread.contact_id !== null ? (
              <Link
                to={`/contacts/${thread.contact_id}`}
                className="truncate text-sm font-semibold hover:text-primary hover:underline"
              >
                {name}
              </Link>
            ) : (
              <span className="truncate text-sm font-semibold">{name}</span>
            )}
            {thread.contact_kind ? (
              <Badge tone={kindTone(thread.contact_kind)}>
                {KIND_LABELS[thread.contact_kind] ?? thread.contact_kind}
              </Badge>
            ) : null}
          </div>
          <div className="text-xs text-muted-foreground">
            {formatPhone(thread.party_number)}
            {thread.client_id ? (
              <>
                {' · '}
                <Link to={`/clients/${thread.client_id}`} className="hover:text-foreground">
                  {thread.client_name}
                </Link>
              </>
            ) : thread.lead_id ? (
              <>
                {' · '}
                <Link to={`/leads/${thread.lead_id}`} className="hover:text-foreground">
                  Lead
                </Link>
              </>
            ) : null}
          </div>
        </div>
        <div className="ml-auto flex items-center gap-1">
          {thread.contact_id === null && !deleted ? (
            <Button size="sm" variant="secondary" onClick={() => setIdentifying(true)}>
              <UserPlus className="size-3.5" /> Identify
            </Button>
          ) : null}
          {!deleted ? (
            <button
              type="button"
              onClick={() => sp.makeCall(thread.party_number)}
              className="inline-flex size-9 items-center justify-center rounded-full text-[var(--color-success)] hover:bg-[var(--color-success)]/10"
              title={`Call ${formatPhone(thread.party_number)}`}
              aria-label="Call"
            >
              <Phone className="size-4" />
            </button>
          ) : null}
          <Menu
            items={
              deleted
                ? [
                    {
                      key: 'restore',
                      label: 'Restore conversation',
                      icon: RotateCcw,
                      onSelect: async () => {
                        await restoreThread.mutateAsync(thread.party_number);
                        onBack();
                        toast.success('Conversation restored');
                      },
                    },
                  ]
                : [
                    {
                      key: 'delete-thread',
                      label: 'Delete conversation',
                      icon: Trash2,
                      danger: true,
                      onSelect: async () => {
                        const ok = await confirm({
                          title: 'Delete this whole conversation?',
                          body: 'Every text to and from this number moves to the deleted list, where you can restore it or empty it for good.',
                          confirmLabel: 'Delete',
                          tone: 'danger',
                        });
                        if (!ok) return;
                        const n = await removeThread.mutateAsync(thread.party_number);
                        onBack();
                        toast.success(`${n} message${n === 1 ? '' : 's'} deleted`);
                      },
                    },
                  ]
            }
          />
        </div>
      </div>

      <IdentifyDialog
        number={thread.party_number}
        open={identifying}
        onClose={() => setIdentifying(false)}
      />

      <div ref={scrollRef} className="flex-1 space-y-2 overflow-y-auto px-4 py-4">
        {isLoading ? (
          <SkeletonList rows={4} />
        ) : (
          (messages ?? []).map((m) => <Bubble key={m.id} message={m} deleted={deleted} />)
        )}
      </div>

      <form onSubmit={handleSend} className="flex items-end gap-2 border-t border-[var(--color-border)] p-3">
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder="Type a message…"
          rows={1}
          className="max-h-32 min-h-[2.5rem] flex-1 resize-none"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              e.currentTarget.form?.requestSubmit();
            }
          }}
        />
        <Button type="submit" disabled={send.isPending || !body.trim()}>
          <Send className="size-4" />
        </Button>
      </form>
    </>
  );
}

function Bubble({ message, deleted }: { message: MessageData; deleted: boolean }) {
  const outbound = message.direction === 'outbound';
  const failed = message.status === 'failed';
  const remove = useDeleteMessage();
  const confirm = useConfirm();

  async function handleDelete() {
    const ok = await confirm({
      title: 'Delete this message?',
      body: 'It moves to the deleted list with the rest of this conversation, where you can restore it or empty it for good.',
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (ok) await remove.mutateAsync(message.id);
  }

  return (
    <div className={cn('group flex items-center gap-1', outbound ? 'justify-end' : 'justify-start')}>
      {/* Left of an outbound bubble, right of an inbound one, so it never
          covers the text. Hidden until the row is hovered. */}
      {outbound && !deleted ? <BubbleDelete onDelete={handleDelete} /> : null}
      <div className="max-w-[75%]">
        <div
          className={cn(
            'rounded-2xl px-3.5 py-2 text-sm',
            outbound
              ? 'bg-[var(--color-primary)] text-white'
              : 'bg-[var(--color-surface-muted)] text-foreground',
            failed && 'bg-[var(--color-danger)]',
          )}
        >
          {message.body ? <p className="whitespace-pre-line">{message.body}</p> : null}
          {message.media.map((media, i) =>
            media.content_type.startsWith('image/') ? (
              <a key={i} href={media.url} target="_blank" rel="noreferrer" className="mt-1 block">
                <img src={media.url} alt="" className="max-h-48 rounded-lg" />
              </a>
            ) : (
              <a key={i} href={media.url} target="_blank" rel="noreferrer" className="mt-1 block underline">
                Attachment
              </a>
            ),
          )}
        </div>
        <div
          className={cn(
            'mt-0.5 text-[10px] text-muted-foreground',
            outbound ? 'text-right' : 'text-left',
          )}
        >
          {failed ? <span className="text-[var(--color-danger)]">Not delivered</span> : formatDate(message.created_at)}
        </div>
      </div>
      {!outbound && !deleted ? <BubbleDelete onDelete={handleDelete} /> : null}
    </div>
  );
}

function BubbleDelete({ onDelete }: { onDelete: () => void }) {
  return (
    <button
      type="button"
      onClick={onDelete}
      aria-label="Delete message"
      title="Delete message"
      className="inline-flex size-7 shrink-0 items-center justify-center rounded-md text-muted-foreground opacity-0 transition-opacity hover:bg-[var(--color-danger)]/10 hover:text-[var(--color-danger)] focus-visible:opacity-100 group-hover:opacity-100"
    >
      <Trash2 className="size-3.5" />
    </button>
  );
}

function NewMessageDialog({
  open,
  onClose,
  onStarted,
}: {
  open: boolean;
  onClose: () => void;
  onStarted: (number: string) => void;
}) {
  const send = useSendMessage();
  const { error, wrap } = useFormError();
  const [to, setTo] = useState('');
  const [body, setBody] = useState('');

  async function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    let created: MessageData | undefined;
    const ok = await wrap(async () => {
      created = await send.mutateAsync({ to, body });
    });
    if (ok && created) {
      setTo('');
      setBody('');
      onStarted(created.to_number);
    }
  }

  return (
    <Dialog open={open} onClose={onClose} title="New message">
      <form onSubmit={handleSubmit} className="space-y-3">
        <label className="block space-y-1.5">
          <span className="text-xs font-medium text-[var(--color-foreground-muted)]">To (phone number)</span>
          <Input
            required
            value={to}
            onChange={(e) => setTo(e.target.value)}
            placeholder="(509) 555-1234"
            autoFocus
          />
        </label>
        <label className="block space-y-1.5">
          <span className="text-xs font-medium text-[var(--color-foreground-muted)]">Message</span>
          <Textarea
            required
            value={body}
            onChange={(e) => setBody(e.target.value)}
            placeholder="Type your message…"
            className="min-h-[100px]"
          />
        </label>
        {error ? <p className="text-sm text-[var(--color-danger)]">{error}</p> : null}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" disabled={send.isPending || !to.trim() || !body.trim()}>
            {send.isPending ? 'Sending…' : 'Send'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
